import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1 min',
    mm: '%d mins',
    h: '1 hour',
    hh: '%d hours',
    d: '1 day',
    dd: '%d days',
    M: '1 month',
    MM: '%d months',
    y: '1 year',
    yy: '%d years'
  }
});

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export function isValidDate(date?: Date | null) {
  return date instanceof Date && date.getTime() == date.getTime();
}

/**
 *
 * @param timeString string time following the format 'h:mm A'
 * @returns valid Date object with the time set to the input timeString
 */
export function createDateFromTimeString(timeString: string) {
  if (!timeString) {
    return;
  }
  const currentDate = dayjs();
  const [time, ampm] = timeString.split(' ');
  if (!time || !ampm) {
    return;
  }
  const [hours, minutes] = time.split(':').map(Number);
  if (hours === undefined || Number.isNaN(hours)) {
    return;
  }
  const timeDetails = {
    hour: (hours % 12) + (ampm.toUpperCase() === 'PM' ? 12 : 0),
    minute: minutes === undefined || Number.isNaN(minutes) ? 0 : minutes
  };
  const newDate = currentDate
    .set('hour', timeDetails.hour)
    .set('minute', timeDetails.minute);
  return newDate.toDate();
}

export const validateMaxDate = (date: Date | Dayjs, maxDate: Date | Dayjs) => {
  const dateJs = dayjs(date);
  const maxDateJs = dayjs(maxDate);
  return dateJs.isSameOrBefore(maxDateJs, 'date');
};

export const validateMinDate = (date: Date | Dayjs, minDate: Date | Dayjs) => {
  const dateJs = dayjs(date);
  const minDateJs = dayjs(minDate);
  return dateJs.isSameOrAfter(minDateJs, 'date');
};

/**
 * Returns a human-readable relative time string (e.g. "5 mins ago", "in 2 days")
 * based on the given date, using a customized Day.js locale.
 *
 * @param date - A date string parseable by Day.js
 * @returns A relative time description compared to now
 */
export const getFromNowFormattedDate = (date: string) => {
  return dayjs(date).fromNow();
};

/**
 * Formats a timestamp for an inline preview of a recent comment:
 *   - today, less than 1 hour ago -> "59 mins ago"
 *   - today, more than 1 hour ago -> "Today, 8:00 AM"
 *   - more than 1 day ago -> "Apr 10, 8:00 AM" (no year)
 */
export const formatCommentPreviewTimestamp = (date: string) => {
  const target = dayjs(date);
  const now = dayjs();
  if (target.isSame(now, 'day')) {
    if (now.diff(target, 'hour') < 1) {
      return target.fromNow();
    }
    return `Today, ${target.format('h:mm A')}`;
  }
  return target.format('MMM D, h:mm A');
};

/**
 * Validates that a date is not in the future (not after today).
 * Useful for form validation with react-hook-form.
 *
 * @param errorMessage - Custom error message to display on validation failure
 * @returns A validation function that returns true if valid, or the error message if invalid
 */
export const validateNotAfterToday = (errorMessage: string) => {
  return (value: string | Date | null | undefined) => {
    if (!value) {
      return true;
    }
    return dayjs(value).isSameOrBefore(dayjs(), 'day') || errorMessage;
  };
};

/**
 * Validates that a date is on or after a reference date (e.g., move-in date).
 * Useful for form validation with react-hook-form.
 *
 * @param referenceDate - The minimum allowed date
 * @param errorMessage - Custom error message to display on validation failure
 * @returns A validation function that returns true if valid, or the error message if invalid
 */
export const validateNotBeforeDate = (
  referenceDate: string | Date | null | undefined,
  errorMessage: string
) => {
  return (value: string | Date | null | undefined) => {
    if (!value || !referenceDate) {
      return true;
    }
    return (
      dayjs(value).isSameOrAfter(dayjs(referenceDate), 'day') || errorMessage
    );
  };
};
